import React, { useEffect, useState } from "react";
import { Tab } from "abs-ui-components";
import AbsModal from "../../../common/absModal/AbsModal";
import MonitoringMethodsTab from "./MonitoringMethodsTab";
import FuelConsumptionTab from "./FuelConsumptionTab";
import AggregatedEmissionDataTab from "./AggregatedEmissionDataTab";
import { getShipLevelReportSummary } from "../../../services/UKETSAerService";
import { emitToasterMsg } from "../../../services/toasterUtility";
import "./ShipLevelDrawer.scss";

const ShipLevelDrawer = ({
	isOpen,
	ship,
	ukEtsAerMstId,
	setIsOpen,
	status,
	onClose,
}) => {
	const [activeTab, setActiveTab] = useState(0);
	const [summaryData, setSummaryData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [fuelConsumptionRows, setFuelConsumptionRows] = useState([]);

	const isReportEditable = status !== "Verification Completed";

	useEffect(() => {
		if (!isOpen || !ship?.reportMstId || !ukEtsAerMstId) {
			return;
		}

		const loadShipLevelSummary = async () => {
			setLoading(true);

			try {
				const result = await getShipLevelReportSummary(
					ukEtsAerMstId,
					ship.reportMstId
				);

				if (result?.isSuccess) {
					setSummaryData(result.data);
					setFuelConsumptionRows(result.data?.fuelConsumptions || []);
				} else {
					setSummaryData(null);
					setFuelConsumptionRows([]);

					emitToasterMsg(
						"error",
						"Error",
						result?.errorMessage ||
							"Failed to load ship-level report details."
					);
				}
			} catch (err) {
				setSummaryData(null);
				setFuelConsumptionRows([]);

				emitToasterMsg(
					"error",
					"Error",
					"Failed to load ship-level report details."
				);
			} finally {
				setLoading(false);
			}
		};

		loadShipLevelSummary();
	}, [isOpen, ship?.reportMstId, ukEtsAerMstId]);

	const handleTabChange = (index) => {
		setActiveTab(index);
	};

	const drawerSubtitle = ship
		? `${ship.vesselName || "—"} · IMO ${ship.imoNumber || "—"}`
		: "";

	// Tab headers for the Tab component
	const tabHeaders = ["Monitoring Methods", "Fuel Consumption", "Aggregated Emission Data"];

	return (
		<div className="ship-level-drawer">
			<AbsModal isOpen={isOpen} title="Ship-Level Emission Report" onClose={setIsOpen} close={onClose} subtitle={drawerSubtitle}>
				<div className="ship-level-drawer__tabs">
					<Tab
						tabData={tabHeaders.map((header, index) => ({
							header: (
								<span className={`ship-level-drawer__tab-label ${activeTab === index ? "active" : ""}`}>
									{header}
								</span>
							),

							content: (
								<>
									<div style={{ display: activeTab === 0 ? "block" : "none" }}>
										<MonitoringMethodsTab
											monitoringMethods={summaryData?.monitoringMethods || []}
											loading = {loading} 
										/>
									</div>

									<div style={{ display: activeTab === 1 ? "block" : "none" }}>
										<FuelConsumptionTab
											fuelConsumptions={fuelConsumptionRows}
											onRowsChange={setFuelConsumptionRows}
											reportMstId={ship?.reportMstId}
											ukEtsCompanyLevelReportId={ukEtsAerMstId}
											isReportEditable={isReportEditable}
											status={status}
										/>
									</div>

									<div style={{ display: activeTab === 2 ? "block" : "none" }}>
										<AggregatedEmissionDataTab
											inPortActivitiesEmissions={summaryData?.inPortActivitiesEmissions}
											voyagesOtherThanSub={summaryData?.voyagesOtherThanSub}
											voyagesBetweenGINI={summaryData?.voyagesBetweenGINI}
											totalMaritimeEmissions={summaryData?.totalMaritimeEmissions}
										/>
									</div>
								</>
							),
						}))}
						onTabChange={handleTabChange}
						activeTab={activeTab}
					/>
				</div>
			</AbsModal>
		</div>
	);
};

export default ShipLevelDrawer;